import { NgClass } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenuItem } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthBffService } from '@visiativ/digital-account-front';
import { MenuEntry } from '../../interfaces/menu-entry';

@Component({
	selector: 'app-user-profile-menu',
	templateUrl: './user-profile-menu.component.html',
	styleUrls: ['./user-profile-menu.component.scss'],
	standalone: true,
	imports: [NgClass, MatMenuItem, MatIcon],
})
export class UserProfileMenuComponent {
	private readonly router = inject(Router);
	private readonly authBffService = inject(AuthBffService);
	@Input() closeSheet?: () => void;

	menuEntries: MenuEntry[] = [];
	currentSubMenu: MenuEntry[] | null = null;
	subMenuTitle: string;

	constructor() {
		this.menuEntries = [
			{
				icon: 'person',
				label: $localize`:@@hub.global.profile-label:Mon profil`,
				actionType: 'function',
				actionValue: this.redirectMyProfile.bind(this, ''),
			},
			{
				icon: 'cookie',
				label: $localize`:@@hub.menu.cookie-preferences-label:Préférence cookies`,
				actionType: 'function',
				actionValue: this.openAxeptioCookies.bind(this),
			},
			{
				icon: 'description',
				label: $localize`:@@hub.global.legal-documents-label:Documents légaux`,
				actionType: 'submenu',
				subMenu: [
					{
						label: $localize`:@@hub.global.legal-cgu-label:CGU`,
						actionType: 'function',
						actionValue: this.redirectToLegalDocuments.bind(this, 'cgu'),
					},
					{
						label: $localize`:@@hub.global.legal-notice-label:Mentions légales`,
						actionType: 'function',
						actionValue: this.redirectToLegalDocuments.bind(this, 'legal_notice'),
					},
					{
						label: $localize`:@@hub.global.legal-privacy-policy-label:Politique de confidentialité`,
						actionType: 'function',
						actionValue: this.redirectToLegalDocuments.bind(this, 'privacy_policy'),
					},
				],
			},
			{
				icon: 'exit_to_app',
				label: $localize`:@@hub.menu.disconnect-label:Se déconnecter`,
				actionType: 'function',
				actionValue: this.disconnect.bind(this),
			},
		];
	}

	public executeAction(entry: MenuEntry) {
		if (entry.actionType === 'function' && typeof entry.actionValue === 'function') {
			entry.actionValue();
		} else if (entry.actionType === 'redirect') {
			window.location.href = entry.actionValue as string;
		} else if (entry.actionType === 'href') {
			window.location.href = entry.actionValue as string;
		}

		this.onClose();
		this.resetSubMenu(null);
	}

	public openSubMenu(event: Event, entry: MenuEntry) {
		event.stopPropagation();

		if (entry.subMenu) {
			this.subMenuTitle = entry.label;
			this.currentSubMenu = entry.subMenu;
		}
	}

	public resetSubMenu(event: Event | null) {
		if (event) {
			event.stopPropagation();
		}
		this.currentSubMenu = null;
	}

	public redirectMyProfile() {
		this.router.navigate(['my_information/']);
	}

	public redirectToLegalDocuments(route: string) {
		let targetRoute = '';

		switch (route) {
			case 'cgu':
				targetRoute = '/legal_documents/cgu';
				break;
			case 'legal_notice':
				targetRoute = '/legal_documents/legal_notice';
				break;
			case 'privacy_policy':
				targetRoute = '/legal_documents/privacy_policy';
				break;
			default:
				targetRoute = '/legal_documents';
				break;
		}

		this.router.navigate([targetRoute]);
	}

	public openAxeptioCookies(): void {
		const openCookiesFunction = (window as { openAxeptioCookies?: () => void }).openAxeptioCookies;

		if (typeof openCookiesFunction === 'function') {
			openCookiesFunction();
		}
	}

	public disconnect(): void {
		this.authBffService.logout();
	}

	public onClose(): void {
		if (this.closeSheet) {
			this.closeSheet();
		}
	}
}
